import type { Resolver, FieldValues } from 'react-hook-form';
import { FieldDef, SelectOption } from './types';

/**
 * contractForm — ponte contract condiviso (zod) → CustomFormV3.
 *
 * `zodToFieldDefs(schema, meta)` genera i FieldDef del form a partire dallo
 * schema zod del contract (`@leopart/shared/contracts/*`) e dal suo
 * FIELD_META di presentazione: una definizione per risorsa da cui derivano
 * DTO backend, tipi FE, config form e vincoli e2e — si debugga una volta sola.
 *
 * NB: le firme sono STRUTTURALI (nessun `import ... from 'zod'` qui): lo
 * shared pinna zod 3.23.8, mentre nel root del monorepo è hoistata un'altra
 * major (i cui d.ts non compilano con la TS 4.9 di CRA). I tipi minimi qui
 * sotto descrivono solo ciò che leggiamo dagli internals di zod 3.
 *
 * TIPI COPERTI (pilota Space) ed ESTENSIBILITÀ:
 *   - ZodString  → 'text-input'
 *   - ZodEnum    → 'single-select' (options da meta.optionLabels, nell'ordine
 *                  del meta, oppure dai valori zod se il meta non le definisce)
 *   - ZodBoolean → 'boolean'
 *   - required   → dal `.optional()` zod (label con suffisso " *")
 *   - wrapper trasparenti: ZodOptional / ZodNullable / ZodDefault / ZodEffects
 *
 * Per aggiungere un tipo (es. ZodNumber → text-input `inputType: 'number'`,
 * ZodDate → 'date', ZodArray(ZodEnum) → 'multi-select'):
 *   1. aggiungi il case in `buildFieldDef`;
 *   2. coprilo in `contractForm.test.ts`.
 * Un tipo non mappato fa THROW esplicito: mai fallback silenzioso.
 */

// ─── Vista strutturale (version-agnostic) degli internals zod ────────────────

interface ZodDefLike {
  typeName?: string;
  /** ZodOptional / ZodNullable / ZodDefault */
  innerType?: ZodTypeLike;
  /** ZodEffects */
  schema?: ZodTypeLike;
  /** ZodEnum */
  values?: readonly string[];
}

export interface ZodTypeLike {
  _def: ZodDefLike;
  isOptional?: () => boolean;
}

export interface ZodObjectLike {
  shape: Record<string, ZodTypeLike>;
}

// ─── Meta di presentazione del contract (vedi SPACE_FIELD_META) ───────────────

export interface ContractFieldMeta {
  label?: string;
  /** Solo presentazione: la verità sul required è lo schema zod. */
  required?: boolean;
  placeholder?: string;
  /** value → label; l'ORDINE delle chiavi è l'ordine delle option nel select. */
  optionLabels?: Readonly<Record<string, string>>;
}

export interface ContractMeta {
  order?: readonly string[];
  fields?: Readonly<Record<string, ContractFieldMeta | undefined>>;
}

export interface ZodToFieldDefsOptions {
  /** colSpan (1–12) dei campi generati. Default 12: campi impilati full-width. */
  size?: number;
  /** Aggiunge " *" alla label dei campi required (default true). */
  requiredMark?: boolean;
}

// ─── Generazione ──────────────────────────────────────────────────────────────

const TRANSPARENT_WRAPPERS = ['ZodOptional', 'ZodNullable', 'ZodDefault', 'ZodEffects'];

/** Scarta i wrapper trasparenti fino al tipo "vero" del campo. */
function unwrap(field: ZodTypeLike): ZodTypeLike {
  let current = field;
  while (TRANSPARENT_WRAPPERS.includes(current._def?.typeName ?? '')) {
    const inner = current._def.innerType ?? current._def.schema;
    if (!inner) break;
    current = inner;
  }
  return current;
}

function buildEnumOptions(
  values: readonly string[],
  fieldMeta?: ContractFieldMeta
): SelectOption[] {
  const labels = fieldMeta?.optionLabels;
  if (!labels) return values.map((value) => ({ value, label: value }));
  // Ordine del meta (presentazione), poi eventuali valori zod non presenti nel
  // meta in coda (mai perdere un valore ammesso dallo schema); chiavi del meta
  // estranee allo schema vengono ignorate.
  const known = Object.keys(labels).filter((v) => values.includes(v));
  const missing = values.filter((v) => !(v in labels));
  return [...known, ...missing].map((value) => ({
    value,
    label: labels[value] ?? value,
  }));
}

function buildFieldDef(
  key: string,
  field: ZodTypeLike,
  fieldMeta: ContractFieldMeta | undefined,
  options: Required<ZodToFieldDefsOptions>
): FieldDef {
  const required = !(typeof field.isOptional === 'function' && field.isOptional());
  const baseLabel = fieldMeta?.label ?? key;
  const label = required && options.requiredMark ? `${baseLabel} *` : baseLabel;
  const base = {
    key,
    label,
    size: options.size,
    ...(fieldMeta?.placeholder ? { placeholder: fieldMeta.placeholder } : {}),
  };

  const unwrapped = unwrap(field);
  const typeName = unwrapped._def?.typeName;
  switch (typeName) {
    case 'ZodString':
      return { ...base, type: 'text-input' };
    case 'ZodEnum':
      return {
        ...base,
        type: 'single-select',
        options: buildEnumOptions(unwrapped._def.values ?? [], fieldMeta),
      };
    case 'ZodBoolean':
      return { ...base, type: 'boolean' };
    default:
      throw new Error(
        `zodToFieldDefs: tipo zod non supportato per il campo "${key}" (${
          typeName ?? 'sconosciuto'
        }). Aggiungi il mapping in contractForm.ts (vedi doc in testa al file).`
      );
  }
}

/**
 * Genera i FieldDef di CustomFormV3 dallo schema zod del contract.
 * L'ordine dei campi è `meta.order` (chiavi non presenti nello schema sono
 * ignorate; campi dello schema fuori da `order` vengono accodati).
 */
export function zodToFieldDefs(
  schema: ZodObjectLike,
  meta?: ContractMeta,
  options?: ZodToFieldDefsOptions
): FieldDef[] {
  const resolved: Required<ZodToFieldDefsOptions> = {
    size: options?.size ?? 12,
    requiredMark: options?.requiredMark ?? true,
  };
  const shape = schema.shape;
  const shapeKeys = Object.keys(shape);
  const order = meta?.order
    ? [
        ...meta.order.filter((key) => shapeKeys.includes(key)),
        ...shapeKeys.filter((key) => !meta.order!.includes(key)),
      ]
    : shapeKeys;
  return order.map((key) =>
    buildFieldDef(key, shape[key], meta?.fields?.[key], resolved)
  );
}

// ─── Resolver RHF minimale (schema zod del contract) ─────────────────────────

interface ZodIssueLike {
  path: (string | number)[];
  code: string;
  message: string;
}

export interface ZodSchemaLike {
  safeParse: (
    values: unknown
  ) =>
    | { success: true; data: unknown }
    | { success: false; error: { issues: ZodIssueLike[] } };
}

/**
 * Mini-resolver RHF che valida con lo STESSO schema zod del contract
 * (schema.safeParse → issues mappate sui campi; prima issue per campo).
 * Non si usa `@hookform/resolvers/zod` perché i suoi d.ts (v5) importano
 * `zod/v3`/`zod/v4/core` dalla zod hoistata nel root — versione diversa da
 * quella pinnata dallo shared e incompatibile con la TS 4.9 di CRA.
 *
 * Limite documentato: le chiavi errore sono path flat ("a.b" per i nested);
 * per contract con oggetti annidati servirà annidare l'oggetto errors.
 */
export function contractResolver<T extends FieldValues = FieldValues>(
  schema: ZodSchemaLike
): Resolver<T> {
  return async (values) => {
    const result = schema.safeParse(values);
    if (result.success) {
      // I dati PARSATI (es. name trimmato) sono ciò che arriva all'onSubmit.
      return { values: result.data as T, errors: {} };
    }
    const errors: Record<string, { type: string; message: string }> = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join('.');
      if (key && !errors[key]) {
        errors[key] = { type: issue.code, message: issue.message };
      }
    }
    return { values: {} as T, errors: errors as never };
  };
}
